import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { Gig_Entity } from "../entities/gig";
import { RootState } from "../redux/store";

export type Ids = {
  id: number;
  sourceRecordID: number;
  sourceID: number;
};

interface RhapsodyState {
  pro?: boolean;
  selectedGigIDs?: Ids | null;
  selectedVenueIDs?: Ids | null;
  selectedSeasonIDs?: Ids | null;
  selectedProjectIDs?: Ids | null;
  selectedContractorIDs?: Ids | null;
  formOpen: { [key: string]: boolean };
  tmpGig?: Partial<Gig_Entity>;
}

const initialState = {
  formOpen: {},
  tmpGig: {},
  pro: false,
} as RhapsodyState;

export const rhapsodySlice = createSlice({
  name: "rhapsody",
  initialState,
  reducers: {
    setPro(state, action: PayloadAction<boolean | null>) {
      state.pro = action.payload;
    },
    setSelectedGigIDs(state, action: PayloadAction<Ids | null>) {
      state.selectedGigIDs = action.payload;
    },
    setSelectedVenueIDs(state, action: PayloadAction<Ids | null>) {
      state.selectedVenueIDs = action.payload;
    },
    setSelectedSeasonIDs(state, action: PayloadAction<Ids | null>) {
      state.selectedSeasonIDs = action.payload;
    },
    setSelectedProjectIDs(state, action: PayloadAction<Ids | null>) {
      state.selectedProjectIDs = action.payload;
    },
    setSelectedContractorIDs(state, action: PayloadAction<Ids | null>) {
      state.selectedContractorIDs = action.payload;
    },
    setFormOpen(
      state,
      action: PayloadAction<{ formID: FormID; isOpen: boolean }>
    ) {
      const { formID, isOpen } = action.payload;
      state.formOpen[formID] = isOpen;
    },
    setTmpGig(state, action: PayloadAction<Partial<Gig_Entity>>) {
      state.tmpGig = action.payload;
    },
  },
});

export const {
  setSelectedGigIDs,
  setFormOpen,
  setTmpGig,
  setSelectedContractorIDs,
  setSelectedProjectIDs,
  setSelectedSeasonIDs,
  setSelectedVenueIDs,
  setPro,
} = rhapsodySlice.actions;

export type FormID =
  | "gig"
  | "upgradeOpen"
  | "deleteMyAccount"
  | "accountCredentials"
  | "smsNotifications"
  | "weeksStartOn"
  | "gig"
  | "venue"
  | "season"
  | "project"
  | "contractor"
  | "changePassword"
  | "passkeys";

export const formOpenSelector = (formID: FormID) => {
  return createSelector([(s) => s.rhapsody.formOpen], (s) => {
    return s[formID] ?? false;
  });
};

export const selectedGigIDsSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedGigIDs,
  (s) => s
);

export const selectedVenueIDsSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedVenueIDs,
  (s) => s
);

export const selectedSeasonIDsSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedSeasonIDs,
  (s) => s
);

export const selectedProjectIDsSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedProjectIDs,
  (s) => s
);

export const proSelector = createSelector(
  (s: RootState) => s.rhapsody.pro,
  (s) => s
);

export const selectedContractorIDsSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedContractorIDs,
  (s) => s
);

export const selectedcontractorIDsSelector = createSelector(
  (s: RootState) => s.rhapsody.selectedContractorIDs,
  (s) => s
);

export const tmpGigSelector = createSelector(
  (s: RootState) => s.rhapsody.tmpGig,
  (s) => s
);

export const endpointSelector = createSelector(
  (s: RootState) => s.networkState.networkApiValues.endpoint,
  (s) => s
);
